import React, { useState, useEffect } from "react";
import client from "@/api/client";
import { BookMarked, Shield, Search, ChevronDown, ChevronUp, Hash, Globe, Info } from "lucide-react";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
const METHOD_STATUS = {
  draft: { label: "Draft", class: "bg-slate-100 text-slate-600" },
  under_review: { label: "Under Review", class: "bg-amber-50 text-amber-700" },
  approved_active: { label: "Approved Active", class: "bg-emerald-50 text-emerald-700" },
  retired: { label: "Retired", class: "bg-slate-50 text-slate-400" }
};

const CATEGORIES = ["all", "electricity", "fuel", "material", "transport", "waste", "water", "circularity_rule"];
const CAT_COLORS = {
  electricity: "bg-blue-50 text-blue-700",
  fuel: "bg-amber-50 text-amber-700",
  material: "bg-violet-50 text-violet-700",
  transport: "bg-sky-50 text-sky-700",
  waste: "bg-orange-50 text-orange-700",
  water: "bg-cyan-50 text-cyan-700",
  circularity_rule: "bg-teal-50 text-teal-700"
};

const GEO_GROUPS = {
  "Southern Africa": ["ZA", "ZW", "ZM", "MZ", "BW"],
  "East Africa": ["KE", "TZ", "UG", "RW", "ET"],
  "West Africa": ["NG", "GH", "CI", "SN"],
  "North Africa": ["EG", "MA"],
  "Global / Other": ["global", "SSA"]
};

export default function Methodologies() {
  const [methodologies, setMethodologies] = useState([]);
  const [factors, setFactors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("methodologies");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [expandedFactor, setExpandedFactor] = useState(null);
  const [geoFilter, setGeoFilter] = useState("all");

  useEffect(() => {
    Promise.all([
      client.get("/methodologies").then(r => setMethodologies(r.data)).catch(() => {}),
      client.get("/factors").then(r => setFactors(r.data)).catch(() => {})
    ]).finally(() => setLoading(false));
  }, []);

  const filtered = factors.filter(f => {
    const matchSearch = !search ||
      f.name?.toLowerCase().includes(search.toLowerCase()) ||
      f.geography_name?.toLowerCase().includes(search.toLowerCase()) ||
      f.source?.toLowerCase().includes(search.toLowerCase());
    const matchCat = categoryFilter === "all" || f.category === categoryFilter;
    const matchGeo = geoFilter === "all" || f.geography === geoFilter;
    return matchSearch && matchCat && matchGeo;
  });

  // Get unique geographies from factors
  const allGeos = [...new Set(factors.map(f => f.geography).filter(Boolean))].sort();

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-fade-in">
        
      <PageHeader
        title="Methodologies & Standards"
        subtitle="Approved accounting methodologies and versioned emission factor library."
      />

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Active Methodologies", value: methodologies.filter(m => m.status === "approved_active").length },
          { label: "Approved Factors", value: factors.filter(f => f.status === "approved_active").length },
          { label: "Geographies Covered", value: new Set(factors.map(f => f.geography)).size }
        ].map(({ label, value }) => (
          <MetricCard
              label={label}
              value={value}
              color="text-slate-900"
            />
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border-b border-slate-200">
        {[["methodologies", "Methodologies"], ["factors", "Factor Library"]].map(([k, l]) => (
          <button key={k} onClick={() => setActiveTab(k)} data-testid={`methods-tab-${k}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === k ? "border-[#059669] text-[#059669]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {l}
          </button>
        ))}
      </div>

      {/* Methodologies */}
      {activeTab === "methodologies" && (
        <div className="space-y-4" data-testid="methodologies-list">
          {loading ? <div className="h-40 bg-white border border-slate-200 rounded-xl animate-pulse" /> :
            methodologies.length === 0 ? (
              <SectionCard>

                <BookMarked size={28} className="mx-auto mb-2 opacity-40" />
                <p className="text-sm">No methodologies available.</p>
                          </SectionCard>
            ) : methodologies.map(m => (
              <div key={m.id} className="bg-white border border-slate-200 rounded-xl p-7" data-testid={`methodology-${m.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <h3 className="text-base font-semibold text-slate-900">{m.name}</h3>
                    <span className="text-xs text-slate-400 font-mono bg-slate-100 px-2 py-0.5 rounded">v{m.version}</span>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${METHOD_STATUS[m.status]?.class || "bg-slate-100 text-slate-600"}`}>
                    {METHOD_STATUS[m.status]?.label || m.status}
                  </span>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-4">{m.description}</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-xs">
                  {[
                    { k: "Standard", v: m.standard },
                    { k: "Scope Coverage", v: m.scope },
                    { k: "Applicability", v: m.applicability }
                  ].filter(x => x.v).map(({ k, v }) => (
                    <div key={k} className="p-3 bg-slate-50 border border-slate-100 rounded-lg">
                      <p className="text-slate-400 mb-0.5 uppercase tracking-wider text-xs">{k}</p>
                      <p className="text-slate-700 font-medium">{v}</p>
                    </div>
                  ))}
                </div>
                {m.status === "approved_active" && (
                  <div className="mt-4 flex items-center gap-2 text-xs text-emerald-600">
                    <Shield size={12} /> Approved and active — all calculations must reference this methodology version
                  </div>
                )}
              </div>
            ))
          }
        </div>
      )}

      {/* Factor Library */}
      {activeTab === "factors" && (
        <div className="space-y-4" data-testid="factors-section">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search factors..." data-testid="factor-search"
                className="w-full h-9 pl-8 pr-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669]" />
            </div>
            <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
              className="h-9 px-3 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none">
              {CATEGORIES.map(c => <option key={c} value={c} className="capitalize">{c === "all" ? "All Categories" : c.replace(/_/g, " ")}</option>)}
            </select>
            <select value={geoFilter} onChange={e => setGeoFilter(e.target.value)}
              className="h-9 px-3 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none">
              <option value="all">All Geographies</option>
              {Object.entries(GEO_GROUPS).map(([group, codes]) => (
                <optgroup key={group} label={group}>
                  {codes.filter(c => allGeos.includes(c)).map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/* Table */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="factors-table">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Factor</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 hidden sm:table-cell">Category</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Value</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 hidden md:table-cell">Unit</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 hidden lg:table-cell">Geography</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 hidden xl:table-cell">Version</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {filtered.length === 0 ? (
                    <tr><td colSpan={8} className="px-5 py-8 text-center text-sm text-slate-400">No factors match your filters</td></tr>
                  ) : filtered.map(f => (
                    <React.Fragment key={f.id}>
                      <tr className="hover:bg-slate-50 cursor-pointer" onClick={() => setExpandedFactor(expandedFactor === f.id ? null : f.id)} data-testid={`factor-row-${f.id}`}>
                        <td className="px-5 py-3">
                          <p className="text-sm font-medium text-slate-900">{f.name}</p>
                          {f.source && <p className="text-xs text-slate-400">{f.source}{f.source_year ? ` ${f.source_year}` : ""}</p>}
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CAT_COLORS[f.category] || "bg-slate-100 text-slate-600"}`}>
                            {f.category?.replace(/_/g, " ")}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm font-bold tabular-nums text-right text-slate-900">{f.value}</td>
                        <td className="px-4 py-3 text-xs text-slate-500 hidden md:table-cell">{f.unit}</td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <div className="flex items-center gap-1">
                            <Globe size={11} className="text-slate-400" />
                            <span className="text-xs font-semibold text-slate-600">{f.geography?.toUpperCase()}</span>
                            {f.geography_name && <span className="text-xs text-slate-400">· {f.geography_name}</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden xl:table-cell">
                          <span className="text-xs font-mono text-slate-500">v{f.current_version || 1}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${f.status === "approved_active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                            {f.status === "approved_active" ? "Active" : f.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {expandedFactor === f.id ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
                        </td>
                      </tr>
                      {expandedFactor === f.id && (
                        <tr className="bg-slate-50">
                          <td colSpan={8} className="px-5 py-4">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
                              <div><p className="text-slate-400 mb-0.5">Input Unit</p><p className="font-medium">{f.input_unit}</p></div>
                              <div><p className="text-slate-400 mb-0.5">Effective From</p><p className="font-medium">{f.effective_from || "—"}</p></div>
                              <div><p className="text-slate-400 mb-0.5">Current Version</p><p className="font-mono font-medium">v{f.current_version || 1}</p></div>
                              <div><p className="text-slate-400 mb-0.5">Source Year</p><p className="font-medium">{f.source_year || "—"}</p></div>
                            </div>
                            {f.description && <p className="text-xs text-slate-500 mt-2 leading-relaxed">{f.description}</p>}
                            {f.applicability && <p className="text-xs text-slate-400 mt-1"><span className="font-medium">Applicability:</span> {f.applicability}</p>}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p className="text-xs text-slate-400">{filtered.length} of {factors.length} factors shown</p>
        </div>
      )}
    </div>
  );
}
